import { useParams, Link } from 'react-router-dom';
import { allMovies } from '@/mocks/movies';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import PageSEO from '@/components/base/PageSEO';
import { getArticleSchema, getBreadcrumbSchema, getWebPageSchema } from '@/mocks/seo';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = allMovies.find(m => m.id === id);

  if (!movie) {
    return (
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">内容未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该影视内容不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i>返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedMovies = allMovies
    .filter(m => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 4);

  return (
    <>
      <PageSEO
        schemas={[
          getWebPageSchema(
            `${movie.title} - 春色影视高清在线观看`,
            movie.description,
            `https://chunseys.com/article/${movie.id}`,
          ),
          getArticleSchema({
            title: movie.title,
            description: movie.description,
            image: movie.image,
            datePublished: movie.updateDate,
            dateModified: movie.updateDate,
            url: `https://chunseys.com/article/${movie.id}`,
          }),
          getBreadcrumbSchema([
            { name: '首页', url: 'https://chunseys.com/' },
            { name: movie.category, url: `https://chunseys.com/category/${movie.categorySlug}` },
            { name: movie.title, url: `https://chunseys.com/article/${movie.id}` },
          ]),
        ]}
      />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: movie.category, href: `/category/${movie.categorySlug}` },
            { label: movie.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="lg:w-2/5 flex-shrink-0">
                <div className="relative aspect-video rounded-lg overflow-hidden">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    title={`${movie.title} - 春色影视高清在线观看`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 bg-background-950/80 text-accent-400 text-lg font-bold px-3 py-1.5 rounded whitespace-nowrap">
                    {movie.rating}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-background-950/40 opacity-0 hover:opacity-100 transition-opacity duration-300 cursor-pointer">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90">
                      <i className="ri-play-fill text-3xl md:text-4xl text-background-50"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-3/5">
                <h1 className="text-xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  {movie.title}
                </h1>

                <div className="flex items-center flex-wrap gap-2 mb-4">
                  {movie.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">导演：</span>
                    <span className="text-sm text-foreground-800">{movie.director}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">年份：</span>
                    <span className="text-sm text-foreground-800">{movie.year}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">地区：</span>
                    <span className="text-sm text-foreground-800">{movie.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">时长：</span>
                    <span className="text-sm text-foreground-800">{movie.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">类型：</span>
                    <span className="text-sm text-foreground-800">{movie.category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">评分：</span>
                    <span className="text-sm text-accent-600 font-semibold">{movie.rating}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">主演：</span>
                  <span className="text-sm text-foreground-800">{movie.actors.join(' / ')}</span>
                </div>

                <div className="p-4 rounded-lg bg-background-100 border border-background-200/70 mb-6">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">剧情简介</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
                </div>

                <Link
                  to={`/category/${movie.categorySlug}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-fill"></i>立即观看
                </Link>
              </div>
            </div>

            <div className="mt-10 md:mt-14">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}